import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type ProposalQueueItem } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, Button, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

function handle(id: string) {
  return `#${id.replace(/-/g, '').slice(-4).toUpperCase()}`;
}

export default function AdminProposalsPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<ProposalQueueItem | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const q = useApi(() => admin.proposalQueue(undefined, 100), [refreshKey]);
  const rows = q.status === 'success' ? q.data : [];

  async function decide(verdict: 'accept' | 'reject') {
    if (!selected) return;
    if (verdict === 'reject' && !notes.trim()) { toast.error(t('reason')); return; }
    setBusy(true);
    try {
      await admin.proposalDecision(selected.id, { verdict, notes: notes.trim() || null });
      toast.success(verdict === 'accept' ? t('proposals.adopted') : t('action.decline'));
      setSelected(null); setNotes('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<ProposalQueueItem>[] = [
    { key: 'proposer_id', header: t('col.proposer'), width: '96px', mono: true,
      render: (r) => handle(r.proposer_id) },
    { key: 'stem', header: t('col.stem'), render: (r) => r.stem },
    { key: 'category', header: t('col.category'), width: '120px', render: (r) => r.category ?? '—' },
  ];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('proposals.title')}</h1>
        <p className="admin-page__sub">{t('proposals.subtitle')}</p>
      </header>

      <div className="admin-md">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => { setSelected(r); setNotes(''); }}
          selectedKey={selected?.id ?? null}
          loading={q.status === 'loading'}
          empty={t('allClear')}
        />

        <aside className="admin-detail">
          {!selected ? (
            <p className="admin-detail__hint">{t('proposals.selectHint')}</p>
          ) : (
            <>
              <h3>{t('proposals.proposer')}: {handle(selected.proposer_id)}</h3>
              <p className="admin-detail__stem">{selected.stem}</p>
              <ul className="admin-detail__choices">
                {selected.choices.map((c) => (
                  <li
                    key={c.label}
                    className={`admin-detail__choice${c.label === selected.correct_choice ? ' is-correct' : ''}`}
                  >
                    <strong>{c.label}.</strong> {c.text}
                  </li>
                ))}
              </ul>
              {selected.explanation && (
                <p className="admin-detail__stem"><em>{selected.explanation}</em></p>
              )}
              <div className="admin-field">
                <span className="admin-field__label">{t('notes')}</span>
                <textarea className="prop-textarea" rows={2} value={notes}
                  onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="admin-detail__actions">
                <Button size="sm" variant="accent" disabled={busy} onClick={() => decide('accept')}>
                  {t('action.accept')}
                </Button>
                <Button size="sm" variant="danger" disabled={busy} onClick={() => decide('reject')}>
                  {t('action.decline')}
                </Button>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
